"use client"
import { useState } from "react"
import { asDate, Content } from "@prismicio/client"
import { SliceComponentProps } from "@prismicio/react"
import Section from "@aura-design/system/section"
import Button from "@aura-design/system/button"
import Grid from "@aura-design/system/grid"
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons"
import { EventsByDay } from "@airplan/ui/collection-event"
import useEvent from "@airplan/hooks/events"
import {
  getNumberOfWeeksInMonth,
  getStartOfWeek,
  getEndOfWeek,
  getCurrentYear,
  getCurrentMonth,
  getCurrentWeek,
} from "@airplan/utils/data-view"
/**
 * Props for `SectionEvents`.
 */
export type SectionEventsProps = SliceComponentProps<Content.SectionEventsSlice>

/**
 * Component for "SectionEvents" Slices.
 */
const SectionEvents = ({ slice, context }: SectionEventsProps): JSX.Element => {
  const { PROJECT_UID } = context as { PROJECT_UID: string }

  const [year, setYear] = useState(getCurrentYear())
  const [month, setMonth] = useState(getCurrentMonth())
  const [week, setWeek] = useState(getCurrentWeek(new Date()))

  const events = useEvent({
    uid: PROJECT_UID,
    year: year,
    month: month,
    week: week,
  })

  const handlePreviousWeek = () => {
    setWeek((prevWeek) => {
      if (prevWeek === 1) {
        // If it's the first week of the month, go to the last week of the previous month
        const prevMonth = month === 1 ? 12 : month - 1
        const prevYear = month === 1 ? year - 1 : year
        setMonth(prevMonth)
        setYear(prevYear)
        return getNumberOfWeeksInMonth(prevYear, prevMonth)
      } else {
        return prevWeek - 1
      }
    })
  }

  const handleNextWeek = () => {
    const nextWeek =
      week === getNumberOfWeeksInMonth(year, month) ? 1 : week + 1
    const nextMonth = month === 12 ? 1 : month + 1
    const nextYear = month === 12 ? year + 1 : year

    // Disable the button if the current week is in the next month OR the next week is in the next month
    const isCurrentWeekInNextMonth =
      week === getNumberOfWeeksInMonth(year, month) && nextMonth !== month
    const isNextWeekInNextMonth = nextWeek === 1 && nextMonth !== month

    if (isCurrentWeekInNextMonth || isNextWeekInNextMonth) {
      return // Do nothing if the current or next week is in the next month
    }

    setWeek((nextWeek) => {
      if (nextWeek === getNumberOfWeeksInMonth(year, month)) {
        // If it's the last week of the month, go to the first week of the next month
        setMonth(nextMonth)
        setYear(nextYear)
        return 1
      } else {
        return nextWeek + 1
      }
    })
  }

  const startDate = getStartOfWeek(year, week)
  const endDate = getEndOfWeek(year, week)

  return (
    <Section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-accents-2"
    >
      <div>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Button mode="link" onClick={handlePreviousWeek}>
              <ChevronLeftIcon />
            </Button>
            <div>
              <strong className="font-bold">Semana: </strong>
              {`${startDate} - ${endDate}`}
            </div>
          </div>

          <Button
            mode="link"
            onClick={handleNextWeek}
            isDisabled={week === getNumberOfWeeksInMonth(year, month)}
          >
            <ChevronRightIcon />
          </Button>
        </div>
        <div className="w-full border-solid border-accents-3 border-x-0 border-b-0 mb-3"></div>
        <div>
          <div>
            {events.isLoading || !events.data ? (
              "Cargando..."
            ) : events.isError ? (
              "Ha occurido un error."
            ) : events.data.results.length === 0 ? (
              "No hay eventos disponibles."
            ) : (
              <Grid col="one">
                <EventsByDay events={events} />
              </Grid>
            )}
          </div>
        </div>
      </div>
    </Section>
  )
}

export default SectionEvents
